import { useState, useEffect, useCallback } from "react";
import moment from "moment";
import {
  usePathname,
  useSearchParams,
  useRouter,
  type ReadonlyURLSearchParams,
} from "next/navigation";
import {
  Field,
  Fields,
  FieldType,
  FilterCriteria,
} from "@bagel-web/components";
import { useLocalStorage } from "../util/localStorage";

const getCriteriaFromSearchParams = (
  fields: Fields,
  searchParams: ReadonlyURLSearchParams
) => {
  const criteria: FilterCriteria<string> = {};
  fields.forEach((field) => {
    const value = searchParams.get(field.name);
    if (value) {
      criteria[field.name] = { value };
    }
  });
  return criteria;
};

const getQualifiedPersistencyKey = (persistenceKey: string) => {
  return `filters_${persistenceKey}`;
};

// Registry of the query params owned by every currently-mounted useFilters
// instance, with the latest values each instance has written. URL writes
// overlay these values so that concurrent instances cannot clobber each
// other: router.replace is asynchronous, so an instance that writes while
// another instance's replace is still in flight would otherwise resurrect
// stale values for the other instance's params from its searchParams
// snapshot.
type FilterIntent = {
  fieldNames: Array<string>;
  values: Record<string, string>;
};
const mountedFilterIntents = new Map<symbol, FilterIntent>();

const useFilters = (
  fields: Array<Field>,
  persistenceKey?: string
): {
  filterCriteria: FilterCriteria<string>;
  handleClearFilter: (fieldName: string) => void;
  handleSetFilters: (updatedCriteria: FilterCriteria<string>) => void;
  handleFilterChange: (fieldName: string, value: string) => void;
} => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathName = usePathname();

  const [instanceId] = useState(() => Symbol("useFilters"));
  useEffect(() => {
    return () => {
      mountedFilterIntents.delete(instanceId);
    };
  }, [instanceId]);

  const [filterCriteria, _setFilterCriteria] = useState<FilterCriteria<string>>(
    () =>
      fields.reduce((acc, field) => {
        if (field.defaultValue) {
          acc[field.name] = { value: field.defaultValue };
        }
        return acc;
      }, {} as FilterCriteria<string>)
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const setFilterCriteria = (updatedCriteria: FilterCriteria<string>) => {
    const uc = { ...updatedCriteria };
    fields
      .filter((field) => field.type === FieldType.Date)
      .forEach((field) => {
        if (uc[field.name]?.value !== undefined) {
          uc[field.name] = {
            value: moment(uc[field.name]?.value).format("YYYY-MM-DD"),
          };
        }
      });
    _setFilterCriteria(uc);
  };

  const handleClearFilter = useCallback(
    (fieldName: string) => {
      if (fields.find((field) => field.name === fieldName)?.required) {
        // Do not clear a required filter field
        return;
      }

      const updatedCriteria = { ...filterCriteria };
      delete updatedCriteria[fieldName];

      const dependentFields =
        fields.find((field) => field.name === fieldName)?.dependentFields || [];

      dependentFields.forEach((dependentField) => {
        delete updatedCriteria[dependentField];
      });

      setFilterCriteria(updatedCriteria);
    },
    [filterCriteria]
  );

  const handleSetFilters = useCallback(
    (updatedCriteria: FilterCriteria<string>) => {
      setFilterCriteria(updatedCriteria);
    },
    []
  );

  const handleFilterChange = useCallback(
    (fieldName: string, value: string) => {
      const updatedCriteria = { ...filterCriteria };
      updatedCriteria[fieldName] = { value };

      const dependentFields =
        fields.find((field) => field.name === fieldName)?.dependentFields || [];

      dependentFields.forEach((dependentField) => {
        delete updatedCriteria[dependentField];
      });

      setFilterCriteria(updatedCriteria);
    },
    [filterCriteria]
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }
    const values: Record<string, string> = {};
    fields.forEach((field) => {
      const value = filterCriteria[field.name]?.value;
      if (value) {
        values[field.name] = value;
      }
    });
    mountedFilterIntents.set(instanceId, {
      fieldNames: fields.map((field) => field.name),
      values,
    });
    if (persistenceKey) {
      useLocalStorage<FilterCriteria<string>>().setItem(
        getQualifiedPersistencyKey(persistenceKey),
        filterCriteria
      );
    }
    // Merge with existing search params to preserve params owned by other
    // parts of the page, then overlay the latest values from every mounted
    // useFilters instance so a stale searchParams snapshot cannot revert
    // another instance's in-flight update.
    const params = new URLSearchParams(searchParams);
    mountedFilterIntents.forEach((intent) => {
      intent.fieldNames.forEach((name) => params.delete(name));
      Object.entries(intent.values).forEach(([name, value]) => {
        params.set(name, value);
      });
    });
    params.sort();
    router.replace(params.size ? pathName + "?" + params.toString() : pathName);
  }, [isLoading, fields, filterCriteria, pathName]);

  useEffect(() => {
    const urlCriteria = getCriteriaFromSearchParams(fields, searchParams);
    const storedCriteria = persistenceKey
      ? useLocalStorage<FilterCriteria<string>>().getItem(
          getQualifiedPersistencyKey(persistenceKey)
        )
      : {};

    if (Object.keys(urlCriteria).length > 0) {
      setFilterCriteria(urlCriteria);
    } else if (storedCriteria && Object.keys(storedCriteria).length > 0) {
      setFilterCriteria(storedCriteria);
    }
    setIsLoading(false);
  }, [fields, searchParams.toString()]);

  return {
    filterCriteria,
    handleClearFilter,
    handleSetFilters,
    handleFilterChange,
  };
};

export default useFilters;
